import ReactGA from "react-ga4";
import type { ErrorType } from "@/lib/errorUtils";
import type { EventName, EventParams } from "@/lib/analytics/events";
import { getGa4MeasurementId } from "@/lib/analytics/ga4";
import { getDeviceId, whenDeviceTagged } from "@/lib/device";
import * as Sentry from '@sentry/react'

function isDebugEnabled(): boolean {
  return String(import.meta.env.VITE_ANALYTICS_DEBUG ?? "").toLowerCase() === "true";
}

function safeString(input: unknown, maxLen = 200): string | undefined {
  if (input == null) return undefined;
  const s = String(input);
  if (!s.trim()) return undefined;
  return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
}

function getRoutePath(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.location?.pathname || undefined;
}

function isAnalyticsEnabled(): boolean {
  return Boolean(getGa4MeasurementId());
}

/** Defer the send until the device id is tagged, then embed it in the payload. Fire-and-forget; never blocks or throws. */
function sendAfterDeviceTagged(send: (deviceId: string) => void): void {
  void whenDeviceTagged()
    .then(() => getDeviceId())
    .then(send)
    .catch(() => {
      // Never send an untagged event.
    });
}

export function track<TName extends EventName>(name: TName, params: EventParams[TName]): void {
  // Always safe to call; becomes a noop when GA is not configured or blocked.
  if (!isAnalyticsEnabled()) return;
  if (typeof window === "undefined") return;

  const enriched = {
    ...params,
    route_path: (params as Record<string, unknown>)?.route_path ?? getRoutePath(),
  } as Record<string, unknown>;

  sendAfterDeviceTagged((deviceId) => {
    try {
      const payload = { ...enriched, device_id: deviceId };
      ReactGA.event(name, payload);
      if (isDebugEnabled()) {
        console.debug("[analytics] track", name, payload);
      }
    } catch {
      // Never throw from analytics.
    }
  });
}

export function trackPageView(pathname: string): void {
  if (!isAnalyticsEnabled()) return;
  if (typeof window === "undefined") return;

  // Read at call time so the title pairs with this pathname; guarded against a throwing getter.
  let title: string | undefined;
  try {
    title = document.title;
  } catch {
    title = undefined;
  }

  sendAfterDeviceTagged((deviceId) => {
    try {
      ReactGA.send({
        hitType: "pageview",
        page: pathname,
        title,
        device_id: deviceId,
      });
      if (isDebugEnabled()) {
        console.debug("[analytics] page_view", pathname);
      }
    } catch {
      // Never throw from analytics.
    }
  });
}

export function trackError(input: {
  type?: ErrorType | string;
  message?: unknown;
  source: "window_error" | "unhandledrejection" | "route_error" | "manual";
  stack?: unknown;
}): void {
  track("app_error", {
    error_type: safeString(input.type),
    message: safeString(input.message, 300),
    source: input.source,
    route_path: getRoutePath(),
    stack_present: Boolean(input.stack),
  });
}

let globalHandlersInstalled = false;

export function installGlobalErrorHandlers(): void {
  if (globalHandlersInstalled) return;
  globalHandlersInstalled = true;

  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    trackError({
      type: "runtime",
      message: (event as ErrorEvent).error?.message ?? event.message,
      source: "window_error",
      stack: (event as ErrorEvent).error?.stack,
    });
    Sentry.captureException(event.error);
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = (event as PromiseRejectionEvent).reason;
    trackError({
      type: "unhandledrejection",
      message: reason?.message ?? reason,
      source: "unhandledrejection",
      stack: reason?.stack,
    });
    Sentry.captureException(new Error(reason?.message ?? reason));
  });
}
